import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { listAuditEvents, type AuditEvent } from '@/services/api/admin';

const PAGE_SIZE = 50;

const KNOWN_EVENTS = [
  'auth.login_success',
  'auth.password_reset_requested',
  'auth.password_reset_completed',
  'billing.plan_upgraded',
  'billing.plan_cancelled',
  'billing.plan_refunded',
  'admin.role_changed',
  'share.created',
  'share.revoked',
  'iac.exported',
  'webhook.signature_failed',
] as const;

function timeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// AuditPanel renders the security event log with filtering + pagination.
// Each row expands to show the raw metadata JSON so an investigator can see
// every field the backend emitted without us having to schema-match here.
export function AuditPanel() {
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState('');
  const [actor, setActor] = useState('');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAuditEvents({
        event: event || undefined,
        actor: actor.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [event, actor, offset]);

  useEffect(() => { load(); }, [load]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mt-6">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 rounded-clyra-lg border border-line-default bg-clyra-raised p-4">
        <div className="flex-1 min-w-[200px] space-y-1.5">
          <label className="block text-petit font-medium text-ink-secondary">Event</label>
          <select
            value={event}
            onChange={(e) => { setEvent(e.target.value); setOffset(0); }}
            className="w-full rounded-clyra border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary focus:border-brand-500/40 focus:outline-none"
          >
            <option value="">All events</option>
            {KNOWN_EVENTS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px] space-y-1.5">
          <label className="block text-petit font-medium text-ink-secondary">Actor user ID</label>
          <input
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setOffset(0); load(); } }}
            placeholder="usr_…"
            className="w-full rounded-clyra border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:border-brand-500/40 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => { setOffset(0); load(); }}
          className="rounded-clyra bg-brand-500/15 px-3 py-2 text-tiny font-medium text-brand-400 hover:bg-brand-500/25 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 overflow-hidden rounded-clyra-lg border border-line-default">
        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-ink-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-ink-muted">No events match.</div>
        ) : (
          <table className="w-full text-tiny">
            <thead>
              <tr className="border-b border-line-subtle bg-clyra-base text-left text-micro uppercase tracking-wider text-ink-faint">
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Event</th>
                <th className="px-3 py-2 font-medium">Actor</th>
                <th className="px-3 py-2 font-medium">Target</th>
                <th className="px-3 py-2 font-medium">IP</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const isOpen = !!expanded[it.id];
                return (
                  <>
                    <tr key={it.id} className="border-b border-line-subtle/60 hover:bg-clyra-base/40 transition-colors">
                      <td className="px-3 py-2 text-ink-secondary whitespace-nowrap" title={new Date(it.occurred_at).toLocaleString()}>
                        {timeAgo(it.occurred_at)}
                      </td>
                      <td className="px-3 py-2 font-mono text-ink-primary">{it.event}</td>
                      <td className="px-3 py-2 font-mono text-ink-secondary">{it.actor_user_id ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-ink-secondary">{it.target_id ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-ink-faint">{it.ip ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setExpanded((m) => ({ ...m, [it.id]: !isOpen }))}
                          className="text-tiny text-brand-400 hover:text-brand-500 transition-colors"
                        >
                          {isOpen ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${it.id}-meta`}>
                        <td colSpan={6} className="bg-clyra-base/40 px-3 pb-3">
                          <pre className="overflow-x-auto rounded-clyra bg-clyra-bg p-3 text-micro text-ink-secondary">
                            {JSON.stringify(it.metadata, null, 2)}
                          </pre>
                          {it.user_agent && (
                            <p className="mt-2 text-micro text-ink-faint truncate" title={it.user_agent}>
                              UA: {it.user_agent}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-tiny text-ink-muted">
        <span>{total} event{total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded px-2 py-1 hover:text-ink-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded px-2 py-1 hover:text-ink-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
